(() => {
  if (customElements.get('sw-pasta-creative')) return;
  const normal = value => (value || '').replace(/[\u200e\u200f]/g, '').replace(/\s+/g, ' ').trim();
  const expectedSummaries = ['מה במארז?', 'למי המארז מתאים?', 'המארז כשר?', 'מידות המארז:', 'לתשומת ליבכם:'];
  const groupKeys = ['pastas', 'sauces', 'surprises'];

  class SwPastaCreative extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (document.readyState === 'loading') {
        this.readyListener = () => this.initialize();
        document.addEventListener('DOMContentLoaded', this.readyListener, { once: true });
      } else this.initialize();
    }

    disconnectedCallback() {
      if (this.readyListener) document.removeEventListener('DOMContentLoaded', this.readyListener);
      this.destroy();
    }

    cloneTemplate(name) {
      return this.querySelector(`template[data-swca-${name}]`)?.content.firstElementChild?.cloneNode(true) || null;
    }

    inspect() {
      if (this.dataset.productId !== '15455068913937') return null;
      const product = this.closest('.product-detail[data-product-id="15455068913937"]');
      if (!product) return null;
      const descriptions = product.querySelectorAll('.sw-pasta-academy-description');
      if (descriptions.length !== 1) return null;
      const description = descriptions[0];
      if (description.dataset.swcaOwner) return null;
      const details = Array.from(description.children).filter(node => node.matches('details.sw-wave1-detail'));
      if (details.length !== expectedSummaries.length) return null;
      if (!details.every((detail, index) => normal(detail.querySelector(':scope > summary .sw-wave1-summary-label')?.textContent) === expectedSummaries[index])) return null;
      const body = details[0].querySelector(':scope > .sw-wave1-body');
      if (!body) return null;
      const paragraphs = Array.from(body.children);
      if (paragraphs.length !== 16 || paragraphs.some(node => node.tagName !== 'P')) return null;
      const headings = [[0, '7 סוגי פסטה של אסם בצורות האהובות:'], [8, '2 רטבי PERFECTO של אסם:'], [11, 'עוד הפתעות שהן באמת מאמה מיה!']];
      if (!headings.every(([index, text]) => normal(paragraphs[index].textContent) === text)) return null;
      const tabs = this.cloneTemplate('tabs');
      const shortcuts = this.dataset.shortcuts === 'false' ? null : this.cloneTemplate('shortcuts');
      if (!tabs || tabs.querySelectorAll('[data-swca-tab]').length !== 3 || (this.dataset.shortcuts !== 'false' && !shortcuts)) return null;
      return { product, description, details, body, paragraphs, tabs, shortcuts };
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      const inspected = this.inspect();
      if (!inspected) {
        this.dataset.swcaState = 'skipped';
        return;
      }
      Object.assign(this, inspected);
      this.controller = new AbortController();
      this.builtNodes = [];
      this.originalNodes = Array.from(this.body.childNodes);
      this.uid = this.id || 'swca-pasta';
      this.initialized = true;
      try {
        this.description.dataset.swcaOwner = this.uid;
        this.description.dataset.swcaEnhanced = '';
        if (this.dataset.tabs !== 'false') this.buildTabs();
        if (this.dataset.magnets !== 'false') this.buildMagnetTrigger();
        this.buildBookletTrigger();
        if (this.dataset.faq !== 'false') this.buildFaq();
        if (this.dataset.characters !== 'false') this.buildCharacters();
        if (this.dataset.shortcuts !== 'false') this.buildShortcuts();
        window.addEventListener('pagehide', () => this.closeDialog(false), { signal: this.controller.signal });
        this.dataset.swcaState = 'ready';
      } catch (error) {
        this.destroy();
        this.dataset.swcaState = 'skipped';
      }
    }

    buildTabs() {
      this.tabs.classList.add('swca-built');
      this.tabButtons = Array.from(this.tabs.querySelectorAll('[data-swca-tab]'));
      this.panels = groupKeys.map((key, index) => {
        const panel = document.createElement('section');
        panel.className = `swca-panel swca-panel--${key}`;
        panel.id = `${this.uid}-panel-${key}`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `${this.uid}-tab-${key}`);
        panel.tabIndex = 0;
        const tab = this.tabButtons[index];
        tab.id = `${this.uid}-tab-${key}`;
        tab.setAttribute('aria-controls', panel.id);
        return panel;
      });
      this.panelWrapper = document.createElement('div');
      this.panelWrapper.className = 'swca-panels';
      this.panels.forEach(panel => this.panelWrapper.append(panel));
      this.body.prepend(this.tabs, this.panelWrapper);
      let group = 0;
      this.originalNodes.forEach(node => {
        if (node === this.paragraphs[8]) group = 1;
        if (node === this.paragraphs[11]) group = 2;
        this.panels[group].append(node);
      });
      this.builtNodes.push(this.tabs, this.panelWrapper);
      this.selectTab(0, false);
      this.tabs.addEventListener('click', event => {
        const index = this.tabButtons.indexOf(event.target.closest('[data-swca-tab]'));
        if (index >= 0) this.selectTab(index, false);
      }, { signal: this.controller.signal });
      this.tabs.addEventListener('keydown', event => {
        const index = this.tabButtons.indexOf(event.target);
        if (index < 0) return;
        const rtl = getComputedStyle(this.tabs).direction === 'rtl';
        let next;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = 2;
        if (event.key === 'ArrowRight') next = (index + (rtl ? 2 : 1)) % 3;
        if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : 2)) % 3;
        if (next === undefined) return;
        event.preventDefault();
        this.selectTab(next, true);
      }, { signal: this.controller.signal });
    }

    selectTab(index, focus) {
      this.tabButtons.forEach((tab, position) => {
        const selected = index === position;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[position].hidden = !selected;
      });
      if (focus) this.tabButtons[index].focus({ preventScroll: true });
    }

    buildMagnetTrigger() {
      const trigger = this.cloneTemplate('magnet-trigger');
      if (!trigger || !this.querySelector('template[data-swca-dialog]')) return;
      (this.panels?.[2] || this.body).append(trigger);
      this.builtNodes.push(trigger);
      trigger.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (this.openMagnets(trigger)) event.preventDefault();
      }, { signal: this.controller.signal });
    }

    buildBookletTrigger() {
      const source = this.querySelector('[data-swca-booklet-manifest]');
      const teaser = this.cloneTemplate('booklet-trigger');
      const trigger = teaser?.querySelector('[data-swca-open-booklet]');
      if (!source || !teaser || !trigger || typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
      let pages;
      try { pages = JSON.parse(source.textContent); } catch (error) { return; }
      if (!Array.isArray(pages) || pages.length !== 20) return;
      if (!pages.every(page => {
        try {
          const url = new URL(page.src, document.baseURI);
          const trusted = url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'));
          return trusted && url.protocol === 'https:' && /\.webp$/i.test(url.pathname) && Number.isInteger(page.width) && page.width > 0 && Number.isInteger(page.height) && page.height > 0;
        } catch (error) { return false; }
      })) return;
      if (!this.querySelector('template[data-swca-booklet-dialog]')) return;
      this.bookletPages = pages;
      this.bookletIndex = 0;
      this.details[0].before(teaser);
      this.builtNodes.push(teaser);
      this.bookletTeaser = teaser;
      trigger.addEventListener('click', () => {
        this.stopBookletTeaser();
        this.openModal('booklet', trigger);
      }, { signal: this.controller.signal });
      this.observeBookletTeaser();
    }

    observeBookletTeaser() {
      if (!this.bookletTeaser || this.teaserPlayed || typeof IntersectionObserver === 'undefined') return;
      this.teaserReduce = matchMedia('(prefers-reduced-motion: reduce)');
      const off = () => this.teaserReduce.matches || document.documentElement.dataset.swPastaMotion === 'off' || document.body.dataset.swPastaMotion === 'off';
      const start = () => {
        if (off()) { this.stopBookletTeaser(); return; }
        if (document.hidden) return;
        const images = Array.from(this.bookletTeaser.querySelectorAll('img'));
        if (!this.teaserVisible || this.teaserPlayed || !images.every(image => image.complete && image.naturalWidth > 0)) return;
        this.teaserPlayed = true;
        this.bookletTeaser.setAttribute('data-swca-turning', '');
        this.teaserTimer = setTimeout(() => this.stopBookletTeaser(), 3200);
      };
      this.teaserReduceListener = () => { if (this.teaserReduce.matches) this.stopBookletTeaser(); };
      this.teaserReduce.addEventListener?.('change', this.teaserReduceListener);
      const signal = this.controller.signal;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          if (this.teaserPlayed) this.stopBookletTeaser();
        } else start();
      }, { signal });
      document.addEventListener('sw-pasta-motion-stop', () => this.stopBookletTeaser(), { signal });
      this.bookletTeaser.querySelectorAll('img').forEach(image => image.addEventListener('load', start, { signal }));
      this.teaserObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          this.teaserVisible = entry.isIntersecting && entry.intersectionRatio >= .5;
          if (this.teaserVisible) start();
          else if (this.teaserPlayed) this.stopBookletTeaser();
        }
      }, { threshold: [0, .5] });
      if (off()) this.stopBookletTeaser();
      else this.teaserObserver.observe(this.bookletTeaser);
    }

    stopBookletTeaser() {
      this.teaserPlayed = true;
      this.cleanupBookletTeaser();
    }

    cleanupBookletTeaser() {
      this.bookletTeaser?.removeAttribute('data-swca-turning');
      clearTimeout(this.teaserTimer);
      this.teaserTimer = null;
      this.teaserObserver?.disconnect();
      this.teaserObserver = null;
      this.teaserReduce?.removeEventListener?.('change', this.teaserReduceListener);
    }

    buildCharacters() {
      const lastDetail = this.details[this.details.length - 1];
      const logo = this.cloneTemplate('character-logo');
      const signature = this.cloneTemplate('character-signature');
      if (!lastDetail || !logo || !signature) return;
      this.builtNodes.push(signature, logo);
      this.description.dataset.swcaCharacters = '';
      // Keep the original share block, links and listeners untouched; decorate after it.
      const shares = Array.from(this.product.querySelectorAll('[data-sw-product-share]'))
        .filter(node => node.closest('.product-detail') === this.product);
      const anchor = shares.length === 1 ? shares[0] : lastDetail;
      anchor.after(signature, logo);
    }

    buildFaq() {
      this.description.dataset.swcaFaq = '';
    }

    buildShortcuts() {
      const shipping = this.product.querySelector('.sw-pasta-shipping');
      if (!shipping) this.shortcuts.querySelector('[data-swca-jump="shipping"]')?.remove();
      this.details[0].before(this.shortcuts);
      this.builtNodes.push(this.shortcuts);
      this.shortcuts.addEventListener('click', event => {
        const button = event.target.closest('[data-swca-jump]');
        if (!button) return;
        const key = button.dataset.swcaJump;
        const target = key === 'shipping' ? shipping : this.details[key === 'faq' ? 1 : 0];
        if (!target) return;
        if (target.tagName === 'DETAILS') target.open = true;
        const focusTarget = target.querySelector(':scope > summary') || target.querySelector('summary');
        focusTarget?.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal: this.controller.signal });
    }

    prepareDialog(kind) {
      this.dialogs ||= {};
      if (this.dialogs[kind]) return this.dialogs[kind];
      const dialog = this.cloneTemplate(kind === 'booklet' ? 'booklet-dialog' : 'dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return null;
      this.append(dialog);
      this.dialogs[kind] = dialog;
      const signal = this.controller.signal;
      dialog.querySelector('[data-swca-close]').addEventListener('click', () => this.closeDialog(), { signal });
      dialog.addEventListener('cancel', event => {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog();
      }, { signal });
      dialog.addEventListener('close', () => {
        if (this.dialog === dialog && !dialog.open) this.finishDialogClose(true);
      }, { signal });
      dialog.addEventListener('pointerdown', event => {
        this.backdropPress = this.isOutsideDialog(event);
      }, { signal });
      dialog.addEventListener('click', event => {
        if (this.backdropPress && this.isOutsideDialog(event)) this.closeDialog();
        this.backdropPress = false;
      }, { signal });
      dialog.querySelector('[data-swca-zoom]').addEventListener('click', event => {
        const scroller = dialog.querySelector('.swca-art-scroll');
        const zoomed = scroller.classList.toggle('is-zoomed');
        event.currentTarget.setAttribute('aria-pressed', String(zoomed));
        event.currentTarget.textContent = zoomed ? this.dataset.zoomOut : this.dataset.zoomIn;
        scroller.scrollLeft = 0;
        scroller.scrollTop = 0;
      }, { signal });
      if (kind === 'booklet') {
        dialog.addEventListener('keydown', event => {
          if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !dialog.open || this.dialog !== dialog) return;
          const stops = Array.from(dialog.querySelectorAll('button, [href], [tabindex]')).filter(element => {
            const visibility = getComputedStyle(element).visibility;
            return element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 && visibility !== 'hidden' && visibility !== 'collapse';
          });
          const first = stops[0];
          const last = stops[stops.length - 1];
          const active = document.activeElement;
          const target = event.shiftKey
            ? ((active === first || active === dialog) ? last : null)
            : ((active === last || active === dialog) ? first : null);
          if (!target) return;
          event.preventDefault();
          target.focus({ preventScroll: true });
        }, { signal });
        dialog.querySelector('[data-swca-page-prev]').addEventListener('click', () => this.renderBooklet(this.bookletIndex - 1), { signal });
        dialog.querySelector('[data-swca-page-next]').addEventListener('click', () => this.renderBooklet(this.bookletIndex + 1), { signal });
        const img = dialog.querySelector('[data-swca-booklet-page]');
        img.addEventListener('load', () => {
          dialog.querySelector('[data-swca-booklet-status]').textContent = '';
          img.removeAttribute('aria-busy');
          if (this.modalState && this.dialog === dialog) this.loadAdjacentPages();
        }, { signal });
        img.addEventListener('error', () => {
          dialog.querySelector('[data-swca-booklet-status]').textContent = this.dataset.bookletError;
          img.removeAttribute('aria-busy');
        }, { signal });
      }
      return dialog;
    }

    renderBooklet(index) {
      const dialog = this.dialogs?.booklet;
      if (!dialog || !this.bookletPages || index < 0 || index >= this.bookletPages.length) return;
      this.bookletIndex = index;
      const page = this.bookletPages[index];
      const label = this.dataset.bookletPageLabel.replace('{page}', index + 1).replace('{total}', this.bookletPages.length);
      const img = dialog.querySelector('[data-swca-booklet-page]');
      dialog.querySelector('[data-swca-page-count]').textContent = label;
      dialog.querySelector('[data-swca-page-prev]').disabled = index === 0;
      dialog.querySelector('[data-swca-page-next]').disabled = index === this.bookletPages.length - 1;
      dialog.querySelector('[data-swca-booklet-status]').textContent = this.dataset.bookletLoading;
      img.alt = label;
      img.width = page.width;
      img.height = page.height;
      img.setAttribute('aria-busy', 'true');
      img.src = page.src;
      const scroller = dialog.querySelector('.swca-art-scroll');
      scroller.scrollTop = 0;
      scroller.scrollLeft = 0;
      this.adjacentImages = [];
    }

    loadAdjacentPages() {
      // At most the two immediate neighbors; no 20-page preload or PDF iframe.
      this.adjacentImages = [this.bookletIndex - 1, this.bookletIndex + 1]
        .filter(index => index >= 0 && index < this.bookletPages.length)
        .map(index => { const img = new Image(); img.decoding = 'async'; img.src = this.bookletPages[index].src; return img; });
    }

    isOutsideDialog(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    openMagnets(trigger) {
      return this.openModal('magnets', trigger);
    }

    openModal(kind, trigger) {
      if (this.modalState || document.querySelector('dialog[open]')) return false;
      const dialog = this.prepareDialog(kind);
      if (!dialog) return false;
      const styles = [document.documentElement, document.body].map(element => ({ element, value: element.style.getPropertyValue('overflow'), priority: element.style.getPropertyPriority('overflow') }));
      try {
        this.dialog = dialog;
        dialog.showModal();
        this.modalState = { trigger, x: scrollX, y: scrollY, styles };
        styles.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
        if (kind === 'booklet') this.renderBooklet(this.bookletIndex || 0);
        dialog.querySelector('[data-swca-close]').focus({ preventScroll: true });
        return true;
      } catch (error) {
        if (dialog.open) dialog.close();
        this.finishDialogClose(false);
        return false;
      }
    }

    closeDialog(restoreFocus = true) {
      // Clear restoration state before close, which can dispatch synchronously in polyfills.
      const dialog = this.dialog;
      this.finishDialogClose(restoreFocus);
      if (dialog?.open) dialog.close();
    }

    finishDialogClose(restoreFocus) {
      const state = this.modalState;
      if (!state) return;
      this.modalState = null;
      this.adjacentImages = [];
      const restoreStyles = () => state.styles.forEach(({ element, value, priority }) => {
        if (element.style.getPropertyValue('overflow') !== 'hidden') return;
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      const anotherDialogIsOpen = () => Array.from(document.querySelectorAll('dialog[open]')).some(dialog => dialog !== this.dialog);
      if (anotherDialogIsOpen()) {
        // A later modal owns focus now. Do not unlock or scroll underneath it.
        const observer = new MutationObserver(releaseIfClear);
        function releaseIfClear() {
          if (document.querySelector('dialog[open]')) return;
          observer.disconnect();
          document.removeEventListener('close', releaseIfClear, true);
          restoreStyles();
        }
        document.addEventListener('close', releaseIfClear, true);
        observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
        return;
      }
      restoreStyles();
      window.scrollTo(state.x, state.y);
      if (restoreFocus && state.trigger.isConnected) state.trigger.focus({ preventScroll: true });
    }

    destroy() {
      this.cleanupBookletTeaser();
      this.closeDialog(false);
      this.controller?.abort();
      if (this.panelWrapper?.parentNode && this.originalNodes) this.panelWrapper.before(...this.originalNodes);
      this.builtNodes?.forEach(node => node.remove());
      this.characterClasses?.forEach(({ node, name, had, original }) => {
        if (!had) node.classList.remove(name);
        const tokens = value => (value || '').trim().split(/\s+/).filter(Boolean).join(' ');
        if (tokens(node.getAttribute('class')) === tokens(original)) {
          if (original === null) node.removeAttribute('class');
          else node.setAttribute('class', original);
        }
      });
      this.characterClasses = null;
      Object.values(this.dialogs || {}).forEach(dialog => dialog.remove());
      this.dialogs = null;
      if (this.description && this.uid && this.description.dataset.swcaOwner === this.uid) {
        delete this.description.dataset.swcaOwner;
        delete this.description.dataset.swcaEnhanced;
        delete this.description.dataset.swcaFaq;
        delete this.description.dataset.swcaCharacters;
      }
      this.dialog = null;
      this.panels = null;
      this.panelWrapper = null;
      this.initialized = false;
    }
  }

  customElements.define('sw-pasta-creative', SwPastaCreative);
})();
